/**
 * OVERCAST — what the original actually does when a mission briefs it, recovered
 * whole. Pure arithmetic; `sky-dressing.ts` applies it.
 *
 * This is **authentic**, not a port extension, and it is not behind
 * `ENHANCED SKY`. It is behind REALISTIC WEATHER, which is the switch the
 * original puts on it.
 *
 * ## `sun.c` `0xf1ac`-`0xf2d5`
 *
 * ```
 * f1ac: if (!ds:0x7250) return                 ; not an Overcast mission -> nothing
 * f1b6: z = player->position.z                 ; 32-bit, world units
 * f1c6: if (!REALISTIC_WEATHER) band = 3
 * f1e7: else band = z <  75000 ? 0
 *                 : z <  76502 ? 1
 *                 : z <  78004 ? 2 : 3
 * f2b0: groundColour = terrainColour           ; defaults
 * f2b6: skyColour    = 48
 * f2bb: if (REALISTIC_WEATHER) {
 * f2c2:   if (band < 2) skyColour    = 84      ; under the deck
 * f2d0:   else          groundColour = 84      ; above it, looking down on cloud tops
 *       }
 * f2d5: applySkyColour()
 * ```
 *
 * and the routine it then calls (`0xee95`) is a two-way switch onto
 * `setSkyShades`, whose only two call sites in the whole binary these are:
 *
 * ```
 * ee98: if (skyColour == 48) setSkyShades(48, 63, bandHeights)   ; the blue ramp
 * eeb5: else                 setSkyShades(84, 84, bandHeights)   ; one flat grey
 * ```
 *
 * So overcast is **not** a cloud layer at all — `RBMB.EXE`'s own generator
 * places **zero** clouds for it (`cloud-field.ts`'s `COVER`). It is a sky
 * repaint with an altitude switch:
 *
 * - **Below ~9,375 ft** the sky is one flat `BARON.PAL` **84 `#bababa`**. You
 *   are under the deck and there is no blue.
 * - **Above ~9,750 ft** the sky is the ordinary blue ramp again and the
 *   **ground** is repainted flat 84 — you are on top, looking down at cloud.
 * - Between the two is the switch's own hysteresis, two intermediate bands
 *   1,502 units (187.75 ft) apart.
 *
 * ## And the everyday sky is a banded ramp after all
 *
 * The non-overcast branch is `setSkyShades(48, 63, ds:0x796)` — the sky ramp
 * `time-of-day.ts` inferred, walked with a **16-entry band-height table**,
 * `{6,6,6,6, 5,5,5,5, 4,4,4, 3,3,3, 2,2}`, 69 scanlines in total. That settles
 * a question `time-of-day.ts` left open as "AUTHENTIC MECHANISM, PORT VALUES":
 * the endpoints **are** 48 and 63, they are constants in the binary, and the
 * distribution is a shipped table rather than an exponent. See
 * `enhanced-sky.md` §2.6 — moving `time-of-day.ts` onto the real table is a
 * follow-up for whoever owns that file, not a change this lane made underneath
 * it.
 */

import type { SkyCondition, TimeOfDay } from "@red-opus/engine";
import type { PaletteColour } from "./time-of-day.ts";

/** `clouds.c`'s world unit: an eighth of a foot. */
const WORLD_UNIT_M = 0.125 * 0.3048;

/**
 * `sun.c` `0xf1e7`-`0xf27b`'s four altitude bands, in world units. The decision
 * the code actually makes is `band < 2`, so `BAND_THRESHOLDS[1]` is the only one
 * that changes the picture; the other two are kept because they are what the
 * binary holds and because the hysteresis they describe is real.
 */
export const OVERCAST_BAND_THRESHOLDS_M: readonly number[] = [75000, 76502, 78004].map(
  (units) => units * WORLD_UNIT_M,
);

/** `BARON.PAL` 84 — the one flat grey `setSkyShades(84, 84, ...)` collapses the run to. */
export const OVERCAST_GREY: PaletteColour = 0xbababa;

/**
 * The deck's colour **per hour**, and why the original has nothing to say about it.
 *
 * `setSkyShades(84, 84, ...)` is a constant, so the recovered behaviour is that
 * an overcast midnight is painted the same `#bababa` as an overcast noon. That
 * is not the original being opinionated about night: it is the original having
 * **no time-of-day sky at all**. `time-of-day.ts`'s "DISPROVEN" section settles
 * it — `RB.EXE` holds one `.pal` literal, the sky ramp endpoints 48/63 are
 * constants, and `sun.c`'s day/night switch swaps the *sky objects* (moon in,
 * sun out, stars on) and touches no colour. A 1990 clear midnight is the same
 * blue as noon.
 *
 * So the hour's dimming is a PORT EXTENSION in whole, and it applies under both
 * treatments a player can choose — only the `measured` debug reference keeps the
 * binary's constant, which is what makes it a reference. An overcast deck that
 * ignored the hour was the one sky in the port that stayed at noon while every
 * other one darkened. It is the same bug the
 * enhanced dome already had a fix for and the same fix: `BARON.PAL`'s 64-87
 * neutral ramp is a *brightness* ladder, and an overcast sky is only grey
 * relative to the light there is. `enhanced-sky-look.ts` reads this table as the
 * grey its other three sky words wash toward, for exactly that reason ("mixing a
 * night sky toward a daytime grey would make an overcast midnight brighter than
 * a clear one") — one table, so the deck and the wash can never disagree.
 *
 * Daytime is the binary's own 84 and does not move; the other three are 76, 73
 * and 65 of the same run.
 */
export const OVERCAST_DECK_GREY: Readonly<Record<TimeOfDay, PaletteColour>> = {
  daytime: OVERCAST_GREY,
  dawn: 0x797979,
  dusk: 0x616161,
  night: 0x1c1c1c,
};

/**
 * Which grey the deck is painted at this hour. `treated` is false only under the
 * `measured` debug reference, which draws the one flat daytime picture that was
 * actually captured from the game whatever hour the mission briefs, so it keeps
 * the recovered 84 — the same contract `skyLookFor` has, and deliberately
 * preserved through the treatment collapse: `#bababa` at every hour is what
 * makes this callable as a byte comparison.
 */
export function overcastDeckGrey(timeOfDay: TimeOfDay | undefined, treated: boolean): PaletteColour {
  if (!treated || timeOfDay === undefined) return OVERCAST_GREY;
  return OVERCAST_DECK_GREY[timeOfDay];
}

/**
 * The altitude `sun.c` `0xf1e7` puts the deck at, which is a **recovered
 * number**: `BAND_THRESHOLDS[1]`, the boundary the `band < 2` test at `0xf2c2`
 * actually decides on. Below it the sky is repainted flat grey and above it the
 * *ground* is — so this is the one height in the binary at which the player
 * stops being under the cloud and starts being on top of it, 76,502 world units
 * = **9,562.75 ft**.
 *
 * `overcast-deck.ts` hangs the ENHANCED deck's geometry here for exactly that
 * reason: the drawn sheet and `overcastRepaintFor`'s switch then cross at the
 * same instant by construction, rather than by two constants agreeing.
 */
export const OVERCAST_DECK_ALTITUDE_M: number = OVERCAST_BAND_THRESHOLDS_M[1] ?? 0;

/**
 * `BARON.PAL` **64-87**, verbatim — the 24-entry neutral brightness run every
 * grey in this file is an entry of, read out of `data/baked/palettes/BARON.PAL.json`.
 *
 * It is here rather than inferred because `OVERCAST_DECK_GREY` above is already
 * described as walking it, and because `overcastDeckRamp` shades the ENHANCED
 * deck by **stepping down this run** from the hour's own entry. That is
 * `three/ramp-shade.ts`'s mechanism — the sim shades a face by walking down a
 * ramp from its anchor, never by multiplying a colour — so the deck's structure
 * is made of colours the 1990 palette could actually hold.
 */
export const OVERCAST_RAMP_FIRST_INDEX = 64;

/**
 * The `BARON.PAL` index of one of this ramp's greys, or 64 for a colour that is
 * not on it. `SkyRun` names the index beside the colour so a test can check the
 * pair against the real file, and the overcast deck's grey is chosen by the
 * hour rather than being a constant — so it has to be looked up rather than
 * written down.
 */
export function overcastRampIndex(colour: PaletteColour): number {
  const at = OVERCAST_NEUTRAL_RAMP.indexOf(colour);
  return at < 0 ? OVERCAST_RAMP_FIRST_INDEX : OVERCAST_RAMP_FIRST_INDEX + at;
}

export const OVERCAST_NEUTRAL_RAMP: readonly PaletteColour[] = [
  0x141414, 0x1c1c1c, 0x242424, 0x2c2c2c, 0x343434, 0x3c3c3c, 0x494949, 0x515151, 0x595959, 0x616161, 0x696969,
  0x717171, 0x797979, 0x828282, 0x8a8a8a, 0x929292, 0x9a9a9a, 0xa2a2a2, 0xaaaaaa, 0xb2b2b2, 0xbababa, 0xc7c7c7,
  0xcfcfcf, 0xd7d7d7,
];

/**
 * How many entries of `OVERCAST_NEUTRAL_RAMP` separate the deck's three tones.
 *
 * Three, so the run from `lit` to `shadow` is 48/255 — six VGA DAC steps, well
 * clear of the smallest change the palette can express, and still inside "grey
 * overcast" rather than reading as a second sky word. A deck whose structure is
 * one entry deep is a deck nobody can see, which is the report this lane
 * answers.
 */
const DECK_RAMP_STEP = 3;

/** One walk of `OVERCAST_NEUTRAL_RAMP` about the hour's grey — the ENHANCED deck's paint. */
export interface OvercastDeckRamp {
  /**
   * One entry **above** the hour's grey: the thin, bright patches where a cloud
   * base is thin enough to pass light (G23).
   *
   * The deck shipped with only `lit`, `mid` and `shadow`, i.e. a field that
   * could only ever *darken* the sky's own colour — which is the exact defect
   * the slab lane had already measured and written up next door
   * (`sky-clouds.ts`): a base that can only darken reads as flat, because the
   * face a pilot sees most of the time is already sitting at the ramp's bottom.
   * Its conclusion, verbatim, is that "letting the field brighten as well as
   * darken is what gives a cloud base its texture, and it costs one more
   * `step`".
   *
   * Clamped at the top of the run rather than extended past it: 64-87 is what
   * the palette holds, and an overcast noon already sits three entries from the
   * end.
   */
  readonly bright: PaletteColour;
  readonly lit: PaletteColour;
  readonly mid: PaletteColour;
  readonly shadow: PaletteColour;
}

/**
 * The hour's deck grey and the two entries below it.
 *
 * **`lit` is the hour's own grey, not a brightening of it**, which is what keeps
 * the deck and the dome joined: `overcastRepaintFor` paints the whole sky that
 * entry, so the deck's brightest tone is exactly the colour the sky already is
 * where the sheet runs out toward the horizon. Everything the deck adds is
 * *darker* — which is also the right way round for a cloud base, whose bellies
 * are the dark part and whose thin edges are the bright part.
 *
 * An overcast night collapses to two entries of near-black and then to one,
 * because the ramp ends at 64. That is not a degenerate case to guard: an
 * overcast midnight has no structure in it, and the arithmetic saying so is
 * better than a floor invented to keep three tones apart.
 */
export function overcastDeckRamp(timeOfDay: TimeOfDay | undefined, treated: boolean): OvercastDeckRamp {
  const lit = overcastDeckGrey(timeOfDay, treated);
  const anchor = OVERCAST_NEUTRAL_RAMP.indexOf(lit);
  const at = (offset: number): PaletteColour =>
    OVERCAST_NEUTRAL_RAMP[Math.min(OVERCAST_NEUTRAL_RAMP.length - 1, Math.max(0, anchor + offset))] ?? lit;
  if (anchor < 0) return { bright: lit, lit, mid: lit, shadow: lit };
  return { bright: at(DECK_RAMP_STEP), lit, mid: at(-DECK_RAMP_STEP), shadow: at(-DECK_RAMP_STEP * 2) };
}

/** `BARON.PAL` 48 and 63 — `setSkyShades`' other call site, verbatim. */
export const SKY_RAMP_LO: PaletteColour = 0x7db6c3;
export const SKY_RAMP_HI: PaletteColour = 0xb6b6b6;

/**
 * `ds:0x796` — the 16 band heights in scanlines the blue ramp is walked with,
 * 69 lines in total. Recovered but **not yet drawn**: this port's dome is a
 * continuous gradient rather than a scanline blit, so translating a table of
 * screen rows into elevations needs the original's horizon row, which
 * `lessons.md` puts at **81** of a `y 0..160` 3D window. Left here as the
 * evidence for that follow-up.
 */
export const SKY_BAND_HEIGHTS: readonly number[] = [6, 6, 6, 6, 5, 5, 5, 5, 4, 4, 4, 3, 3, 3, 2, 2];

/** Which of `sun.c`'s four bands an altitude falls in. */
export function overcastBandAt(altitudeM: number): number {
  for (let band = 0; band < OVERCAST_BAND_THRESHOLDS_M.length; band++) {
    if (altitudeM < (OVERCAST_BAND_THRESHOLDS_M[band] ?? 0)) return band;
  }
  return OVERCAST_BAND_THRESHOLDS_M.length;
}

/** What overcast does to the picture at one altitude. Both `undefined` means "leave it alone". */
export interface OvercastRepaint {
  /** Flat colour to paint the whole dome, or `undefined` to leave the hour's own sky. */
  readonly sky: PaletteColour | undefined;
  /** Flat colour to paint the ground, or `undefined` to leave the hour's own ground. */
  readonly ground: PaletteColour | undefined;
}

export const NO_REPAINT: OvercastRepaint = { sky: undefined, ground: undefined };

/**
 * `sun.c` `0xf1ac`'s whole decision. `realisticWeather` off takes the original's
 * own `band = 3` shortcut and then skips both repaints — which is to say an
 * overcast mission with the switch off looks like a clear one, exactly as the
 * manual describes for clouds.
 *
 * `grey` is the deck's colour: the binary's flat 84 by default, or this hour's
 * entry of the same ramp from `overcastDeckGrey`. It paints **both** halves of
 * the switch, because both are the same cloud — the sky under it and the tops
 * seen from above it are lit by the same absent sun.
 */
export function overcastRepaintFor(
  sky: SkyCondition | undefined,
  realisticWeather: boolean,
  altitudeM: number,
  grey: PaletteColour = OVERCAST_GREY,
): OvercastRepaint {
  if (sky !== "overcast" || !realisticWeather) return NO_REPAINT;
  return overcastBandAt(altitudeM) < 2
    ? { sky: grey, ground: undefined }
    : { sky: undefined, ground: grey };
}
